import { Badge } from '@/components/ui/Badge';

/**
 * Woher die Module einer Routine kommen - eine Stelle für Label, Ton und
 * Bedeutung (Muster: `@/components/routines/tier`).
 *
 * Anlass 26.08.2026: `routinen/requirements.yml` behauptete "collections: []"
 * mit der Begründung "Alle Playbooks kommen mit ansible.builtin aus" -
 * während die beiden Windows-Routinen seit dem 19.08.2026
 * `ansible.windows.win_updates` benutzen. Die Herkunft der Module stand damit
 * an keiner Stelle, an der sie jemand sieht.
 *
 * Warum das im UI sichtbar gehört: eine Routine mit Galaxy-Abhängigkeit
 * läuft nur auf einem Rechner, auf dem die Collection installiert ist. Fehlt
 * sie, stirbt der Lauf am ERSTEN TASK mit "couldn't resolve module/action" -
 * das sieht aus wie eine kaputte Routine und ist eine fehlende Installation.
 *
 * Die Anzeige folgt derselben Regel wie beim Reifegrad: gekennzeichnet wird,
 * was eine Einschränkung bedeutet. "Nur Bordmittel" ist der Normalfall und
 * bekommt in Listen kein Badge - eine Marke an jeder Zeile liest niemand.
 */

/** Der Normalfall in Worten - für Spalten, in denen leer nicht taugt. */
export const BORDMITTEL_LABEL = 'nur Bordmittel';

export const BORDMITTEL_MEANING =
  'Nur Bordmittel: die Routine kommt mit ansible-core aus (ansible.builtin). Sie läuft auf jedem Rechner mit Ansible, ohne dass vorher etwas installiert werden muss.';

/** Server-Liste säubern: nur `namespace.name`, klein, ohne Duplikate. */
export function normalizeCollections(
  raw: readonly string[] | null | undefined
): string[] {
  const gesehen = new Set<string>();
  for (const eintrag of raw ?? []) {
    if (typeof eintrag !== 'string') continue;
    const name = eintrag.trim().toLowerCase();
    // ansible.builtin steckt in ansible-core und ist keine Galaxy-Herkunft;
    // die Registry lehnt es ab, aber ein Altbestand in der DB soll die
    // Anzeige nicht zu "kommt aus der Galaxy" umlügen.
    if (!name || name === 'ansible.builtin') continue;
    gesehen.add(name);
  }
  return [...gesehen].sort((a, b) => a.localeCompare(b));
}

/** Was die Kennzeichnung BEDEUTET - als Tooltip an jeder Stelle dieselbe. */
export function collectionsMeaning(
  raw: readonly string[] | null | undefined
): string {
  const namen = normalizeCollections(raw);
  if (namen.length === 0) return BORDMITTEL_MEANING;
  return (
    `Braucht aus der Ansible Galaxy: ${namen.join(', ')}. ` +
    'Die Collection muss auf dem Wartungs-PC installiert sein ' +
    `(ansible-galaxy collection install ${namen.join(' ')}), sonst scheitert ` +
    'der Lauf am ersten Task. Gepflegt im Meta-Header des Playbooks ' +
    '(required_collections).'
  );
}

function testIdProp(testId: string | undefined): { 'data-testid'?: string } {
  return testId === undefined ? {} : { 'data-testid': testId };
}

interface RoutineCollectionsProps {
  collections: readonly string[] | null | undefined;
  'data-testid'?: string;
}

/**
 * Badge für Listen und Kopfzeilen - NUR wenn es etwas zu sagen gibt.
 * Ohne Galaxy-Abhängigkeit kommt bewusst nichts: der Normalfall braucht
 * keine Marke.
 */
export function RoutineCollectionsBadge({
  collections,
  'data-testid': testId,
}: RoutineCollectionsProps): JSX.Element | null {
  const namen = normalizeCollections(collections);
  if (namen.length === 0) return null;

  return (
    <span title={collectionsMeaning(collections)}>
      {/* Ton "primary" wie bei Snapshot-Pflicht: ein Hinweis auf eine
          Voraussetzung, keine Warnung. Rot/gelb ist hier falsch - die
          Collection ist ja normalerweise installiert. */}
      <Badge tone="primary" {...testIdProp(testId)}>
        <span className="font-mono text-[10px]">{namen.join(', ')}</span>
      </Badge>
    </span>
  );
}

/**
 * Zellinhalt der Herkunft-Spalte in der Library.
 *
 * Hier - und nur hier - bekommt auch der Normalfall Text: die Spalte gehört
 * dem Feld, eine leere Zelle läse sich nicht von "Daten fehlen"
 * unterscheiden. Aber als stiller Grauton, nicht als zweites Badge.
 */
export function RoutineCollectionsCell({
  collections,
  'data-testid': testId,
}: RoutineCollectionsProps): JSX.Element {
  const namen = normalizeCollections(collections);
  if (namen.length === 0) {
    return (
      <span
        className="text-xs text-muted"
        title={BORDMITTEL_MEANING}
        data-testid={testId}
      >
        {BORDMITTEL_LABEL}
      </span>
    );
  }
  // Bewusst dasselbe Markup wie im Badge und kein Aufruf davon: dessen
  // Rückgabe ist `JSX.Element | null`, und ein Cast wäre hier nur ein
  // Versprechen an den Compiler, das der nächste Umbau bricht.
  return (
    <span title={collectionsMeaning(collections)}>
      <Badge tone="primary" {...testIdProp(testId)}>
        <span className="font-mono text-[10px]">{namen.join(', ')}</span>
      </Badge>
    </span>
  );
}
